import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Checkbox, Col, DatePicker, Form, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';
import InfoTooltip from 'components/molecules/InfoTooltip';

import { IDodatekDluhDatum } from 'services/dokumenty/dodatek';
import { IZmenEmisiPrikaz, PrikazZmenEmisiGet, PrikazZmenEmisiPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { EEvidenceCp } from 'enums/isin/evidenceCp';
import { ECisalikvynos } from 'enums/isin/udaje/cisalikvynos';
import { EFrekvenceVyplaty } from 'enums/isin/udaje/frekvenceVyplaty';
import { EUrokovaSazba } from 'enums/isin/udaje/urokovaSazba';

import alikVynosSelect from 'data/codebooks/emise/udaje/alikVynosSelect';
import frekvenceVyplatySelect from 'data/codebooks/emise/udaje/frekvenceVyplatySelect';
import { urokovaSazbaDluhopis } from 'data/codebooks/emise/udaje/urokovaSazbaSelectAlt';
import { zarucenieEmiseSelectAlt } from 'data/codebooks/emise/udaje/zarucenieEmiseSelect';

import getLanguageObject from 'utils/getLanguageObject';
import { procentaValidation6 } from 'utils/validations/procentaValidation';

import { frekvenceVyplatyPocet } from '../dodatekPages/DodatekDetail';
import { getDataIsins } from './Zruseni';

interface IZmenaUdajuComponent {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const ZmenaUdaju: React.FC<IZmenaUdajuComponent> = ({ nextStep, zadostID, prikazyData }) => {
    const { dokumenty, cisMeny, language } = useContext(Context);
    const [form] = Form.useForm();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [paginationCurrent, setPaginationCurrent] = useState<number>(1);
    const [pickerVal, setPickerVal] = useState<dayjs.Dayjs>();
    const [data, setData] = useState<IZmenEmisiPrikaz>();
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<any>();
    const isDLT = !selectedIsin?.evidencecp || selectedIsin?.evidencecp === EEvidenceCp.CDCPDLT;
    const [dodatekDates, setDodatekDates] = useState<IDodatekDluhDatum[]>();
    const [dodatekDatesErr, setDodatekDatesErr] = useState<boolean>(false);
    const dluhopisSecondVariant = data?.frekvencen === EFrekvenceVyplaty.X || data?.frekvencen === EFrekvenceVyplaty.NA;

    useEffect(() => {
        if (selectedIsin) {
            form.resetFields(['typprevodu', 'zpuspredaniDVP', 'zpuspredaniDFP']);
            form.setFieldsValue({
                typplatby: selectedIsin.dnom === 'CZK' ? 'TUP' : 'ZAP'
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    const calculateNoOfDates = (frequency: EFrekvenceVyplaty) => {
        if (dodatekDates?.length) {
            setDodatekDates(pv => {
                let prevState = [...pv!];

                if (prevState?.length < frekvenceVyplatyPocet[frequency]) {
                    prevState = [
                        ...prevState!, // Spread the original array elements
                        ...new Array(Math.max(0, frekvenceVyplatyPocet[frequency] - prevState!.length)).fill({}) // Fill the rest with empty objects
                    ];
                } else {
                    prevState!.length = frekvenceVyplatyPocet[frequency];
                }
                return prevState;
            });
        } else {
            setDodatekDates(new Array(frekvenceVyplatyPocet[frequency]).fill({} as IDodatekDluhDatum));
        }
    };

    useEffect(() => {
        setLoading(true);
        PrikazZmenEmisiGet(zadostID)
            .then(resp => {
                setSelectIsin(resp.isin);
                setData(resp);
                resp.zmendatkup.length ? setDodatekDates(resp.zmendatkup) : calculateNoOfDates(resp.frekvencen);
                form.setFieldsValue({
                    ...(resp?.datjist && {
                        datjist: dayjs(resp?.datjist)
                    }),
                    ...(resp?.datexjist && {
                        datexjist: dayjs(resp?.datexjist)
                    }),
                    typdatjist: resp?.typdatjist,
                    pocdnikup: resp?.pocdnikup,
                    nav: resp?.nav,
                    uroktyp: resp?.uroktyp,
                    //  urokvyse: resp?.urokvyse,
                    //  uroksazba: resp?.uroksazba,
                    standard: resp?.standard,
                    jiny: resp?.jiny,
                    frekvence: resp?.frekvence,
                    zaruceniemise: resp?.zaruceniemise,
                    uroktypn: resp?.uroktypn,
                    menap: resp?.menap,
                    menan: resp?.menan,
                    standardn: resp?.standardn
                });
            })
            .catch(() => setData({} as IZmenEmisiPrikaz))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <FormWrapper
                    outForm={form}
                    onFinish={values => {
                        if (values.frekvence) {
                            const isErr = dodatekDates?.filter(e => !e.datexkup || !e.datrozhkup).length;
                            setDodatekDatesErr(!!isErr);
                            if (isErr) return;
                        }
                        setLoading(true);
                        const dataToSend = {
                            ...(values as any),
                            datum: values.datum.format('YYYY-MM-DD'),
                            ...(values.datkurzun && {
                                datkurzun: values.datkurzun.format('YYYY-MM-DD')
                            }),
                            ...(values.sazbaplatnost && {
                                sazbaplatnost: values.sazbaplatnost.format('YYYY-MM-DD')
                            }),
                            ...(values.datjist && {
                                datjist: values.datjist.format('YYYY-MM-DD')
                            }),
                            ...(values.datexjist && {
                                datexjist: values.datexjist.format('YYYY-MM-DD')
                            }),
                            ...(values.frekvence && {
                                zmendatkup: dodatekDates
                            })
                        };
                        delete dataToSend.idmbSpolecnost;
                        delete dataToSend.dicdphSpolecnost;
                        delete dataToSend.nazevSpolecnost;
                        delete dataToSend.leiSpolecnost;
                        PrikazZmenEmisiPut(zadostID, dataToSend)
                            .then(() => nextStep())
                            .finally(() => setLoading(false));
                    }}
                >
                    <Title text="EMITENT" level={5} />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.idmb}
                        label="IDENTIFIKACNI_CISLO"
                        name="idmbSpolecnost"
                        notRequired={true}
                    />
                    {prikazyData?.spolecnost.dicdph && (
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.dicdph}
                            label="ISIN_IDENTIFIKACE_DIC"
                            name="dicdphSpolecnost"
                            notRequired={true}
                        />
                    )}

                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.nazev}
                        label="ISIN_IDENTIFIKACE_FIRMA"
                        name="nazevSpolecnost"
                        notRequired={true}
                    />
                    <InputComponent
                        disabled={true}
                        initialValue={prikazyData?.spolecnost.lei}
                        label="LEI_CELE"
                        name="leiSpolecnost"
                        notRequired={true}
                    />
                    <SelectComponent
                        name="isin"
                        labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                        defaultValue={data?.isin}
                        data={dataIsins}
                        isFormItem={true}
                        onChange={val =>
                            setSelectIsin(JSON.parse(getDataIsins(dokumenty).filter(e => e.isin === val)[0].data))
                        }
                    />
                    <DatePickerComponent
                        name="datum"
                        initialValue={data?.datum ? dayjs(data.datum) : undefined}
                        // disabledDate={current => current < dayjs().add(4, 'days')}
                        label="PRIKAZ_PREVOD_DATUM"
                    />
                    <Form.Item
                        style={{
                            fontWeight: 600
                        }}
                        name="zmena"
                        label={<TranslateText>PRIKAZ_ZMENA_UDAJU_TYP</TranslateText>}
                        valuePropName="checked"
                        rules={[
                            () => ({
                                required: true,
                                validator() {
                                    if (
                                        !form.getFieldValue('nav') &&
                                        !form.getFieldValue('uroktyp') &&
                                        !form.getFieldValue('urokvyse') &&
                                        !form.getFieldValue('uroksazba') &&
                                        !form.getFieldValue('standard') &&
                                        !form.getFieldValue('jiny') &&
                                        !form.getFieldValue('frekvence') &&
                                        !form.getFieldValue('zaruceniemise')
                                    )
                                        return Promise.reject(<TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>);
                                    return Promise.resolve();
                                }
                            })
                        ]}
                    >
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="nav"
                                initialValue={data?.nav || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_NAV</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="uroktyp"
                                initialValue={data?.uroktyp || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_UROKSAZBATYP</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>

                        {/* <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="urokvyse"
                                    initialValue={data?.urokvyse || false}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => {
                                            rerender(pv => !pv);
                                            form.validateFields(['zmena']);
                                        }}
                                    >
                                        <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_UROKOVASAZBAVYSE</TranslateText>
                                    </Checkbox>
                                </Form.Item>
                            </Row>
                            <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="uroksazba"
                                    initialValue={data?.uroksazba || false}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => {
                                            rerender(pv => !pv);
                                            form.validateFields(['zmena']);
                                        }}
                                    >
                                        <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_UROKOVASAZBA</TranslateText>
                                    </Checkbox>
                                </Form.Item>
                            </Row> */}
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="standard"
                                initialValue={data?.standard || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_AUV</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="frekvence"
                                initialValue={data?.frekvence || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_FREKVENCE</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="zaruceniemise"
                                initialValue={data?.zaruceniemise || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_ZARUCENI_EMISE</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>

                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600,
                                    marginBottom: 22
                                }}
                                name="jiny"
                                initialValue={data?.jiny || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        form.validateFields(['zmena']);
                                    }}
                                >
                                    <TranslateText>PRIKAZ_ZMENA_UDAJU_TYP_JINY</TranslateText>
                                </Checkbox>
                            </Form.Item>
                        </Row>
                    </Form.Item>

                    {form.getFieldValue('nav') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_NAV" />
                            <Title level={5} text="PUVODNI_UDAJE" />
                            <InputNumberComponent
                                name="navakciep"
                                label="DODATEK_INVCERT_NAV"
                                initialValue={data?.navakciep}
                                precision={4}
                            />
                            <SelectComponent
                                name="menap"
                                labelParagraph="DODATEK_INVCERT_NAV_MENA"
                                isFormItem={true}
                                data={cisMeny}
                                onChange={() => rerender(pv => !pv)}
                                defaultValue={data?.menap}
                            />
                            <Title level={5} text="NOVY_UDAJE" />
                            <InputNumberComponent
                                name="navakcien"
                                label="DODATEK_INVCERT_NAV"
                                initialValue={data?.navakcien}
                                precision={4}
                            />
                            <SelectComponent
                                name="menan"
                                labelParagraph="DODATEK_INVCERT_NAV_MENA"
                                isFormItem={true}
                                data={cisMeny}
                                onChange={() => rerender(pv => !pv)}
                                defaultValue={data?.menan}
                            />
                            {form.getFieldValue('menan') && form.getFieldValue('menan') !== 'CZK' && (
                                <DatePickerComponent
                                    name="datkurzun"
                                    initialValue={data?.datkurzun ? dayjs(data.datkurzun) : undefined}
                                    label="DODATEK_INVCERT_NAV_DATUM"
                                />
                            )}
                        </>
                    )}
                    {form.getFieldValue('uroktyp') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_UROKSAZBATYP" />
                            <SelectComponent
                                isFormItem={true}
                                name="uroktypp"
                                defaultValue={data.uroktypp}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_UROKTYP_PUV"
                                data={urokovaSazbaDluhopis}
                            />
                            <SelectComponent
                                isFormItem={true}
                                name="uroktypn"
                                defaultValue={data.uroktypn}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_UROKTYP_NOVY"
                                data={urokovaSazbaDluhopis}
                                onChange={() => {
                                    rerender(pv => !pv);
                                }}
                            />
                            {form.getFieldValue('uroktypn') === EUrokovaSazba.J && (
                                <InputComponent
                                    label="PRIKAZ_ZMENA_UDAJU_TYP_UROKOVASAZBA"
                                    name="uroktyppopis"
                                    initialValue={data?.uroktyppopis}
                                    maxLength={500}
                                />
                            )}

                            {(form.getFieldValue('uroktypn') === EUrokovaSazba.F ||
                                form.getFieldValue('uroktypn') === EUrokovaSazba.V) && (
                                <InputNumberComponent
                                    label="ISIN_EMISE_DLUHOPIS_SAZBA_VYSE"
                                    name="uroktypsazba"
                                    rules={procentaValidation6(true)}
                                    initialValue={data?.uroktypsazba}
                                    maxLength={9}
                                    precision={6}
                                />
                            )}
                        </>
                    )}
                    {form.getFieldValue('urokvyse') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_UROKOVASAZBAVYSE" />
                            <InputNumberComponent
                                initialValue={data?.sazbap}
                                label="PRIKAZ_ZMENA_UDAJU_VYSE_SAZBY_P"
                                name="sazbap"
                                rules={procentaValidation6(true)}
                                maxLength={9}
                                precision={6}
                            />
                            <InputNumberComponent
                                initialValue={data?.sazban}
                                label="PRIKAZ_ZMENA_UDAJU_VYSE_SAZBY_N"
                                name="sazban"
                                rules={procentaValidation6(true)}
                                maxLength={9}
                                precision={6}
                            />
                        </>
                    )}
                    {form.getFieldValue('uroksazba') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_UROKOVASAZBA" />
                            <InputComponent
                                initialValue={data?.urokpopisp}
                                label="PRIKAZ_ZMENA_UDAJU_UROK_SAZBA_P"
                                name="urokpopisp"
                                maxLength={500}
                            />
                            <InputComponent
                                initialValue={data?.urokpopisn}
                                label="PRIKAZ_ZMENA_UDAJU_UROK_SAZBA_N"
                                name="urokpopisn"
                                maxLength={500}
                            />
                        </>
                    )}
                    {(form.getFieldValue('uroksazba') ||
                        form.getFieldValue('uroktyp') ||
                        form.getFieldValue('urokvyse')) && (
                        <DatePickerComponent
                            name="sazbaplatnost"
                            initialValue={data?.sazbaplatnost ? dayjs(data.sazbaplatnost) : undefined}
                            disabledDate={current => current < dayjs()}
                            label="PRIKAZ_ZMENIT_PLATNOST_UROK_SAZBY"
                        />
                    )}

                    {form.getFieldValue('standard') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_AUV" />
                            <SelectComponent
                                isFormItem={true}
                                name="standardp"
                                defaultValue={data?.standardp}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_TYP_STANDARD_PUV"
                                data={alikVynosSelect}
                            />
                            <SelectComponent
                                isFormItem={true}
                                name="standardn"
                                defaultValue={data?.standardn}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_TYPSTANDARD_NOVY"
                                data={alikVynosSelect}
                                onChange={rerender}
                            />
                            {form.getFieldValue('standardn') === ECisalikvynos._13 && (
                                <InputComponent
                                    label="ISIN_EMISE_CERTIFIKAT_SAZBA_POPISTE"
                                    name="standardpopis"
                                    initialValue={data?.standardpopis}
                                    maxLength={500}
                                />
                            )}
                        </>
                    )}
                    {form.getFieldValue('frekvence') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_FREKVENCE" />
                            <SelectComponent
                                isFormItem={true}
                                name="frekvencen"
                                defaultValue={data?.frekvencen}
                                labelParagraph="ISIN_EMISE_DLUHOPIS_FREKVENCE_NOVA"
                                data={frekvenceVyplatySelect}
                                onChange={val => calculateNoOfDates(val)}
                            />
                            <Title level={5} text="DATUM_VYPLATA_KOUPONU" />
                            <InputNumberComponent
                                name="pocdnikup"
                                label="POCET_DNI"
                                initialValue={data?.pocdnikup}
                                rules={[
                                    () => ({
                                        validator(_a: any, value: string) {
                                            if (!value) return Promise.resolve();
                                            if (Number(value) < 0)
                                                return Promise.reject(<TranslateText>POCET_DNI_ERR</TranslateText>);
                                            return Promise.resolve();
                                        }
                                    })
                                ]}
                                onChangeCustom={val => {
                                    if (val || val == 0) {
                                        setDodatekDates(pv =>
                                            pv?.map(e => {
                                                if (e.datrozhkup) {
                                                    return {
                                                        datexkup: dayjs(e.datrozhkup)
                                                            .subtract(Number(val) - 1, 'days')
                                                            .format('YYYY-MM-DD'),
                                                        datrozhkup: e.datrozhkup
                                                    };
                                                }
                                                return e;
                                            })
                                        );
                                        const datjist = form.getFieldValue('datjist');
                                        form.getFieldValue('typdatjist') === 'KUPON' &&
                                            datjist &&
                                            form.setFieldsValue({
                                                datexjist: dayjs(datjist).subtract(Number(val) - 1, 'days')
                                            });
                                    }
                                }}
                            />
                            <Title text="ROZHODNE_DATUM_VYPLATY" level={5} dividerTitle={true}>
                                {dluhopisSecondVariant && (
                                    <ButtonComponent
                                        id="c2PlusAltBtn"
                                        htmlType="button"
                                        type="primary"
                                        style={{
                                            display: 'inline-block',
                                            width: 50
                                        }}
                                        icon={dluhopisSecondVariant ? <PlusOutlined /> : undefined}
                                        onClick={() => {
                                            setDodatekDates(pv => {
                                                return [...pv!, {} as IDodatekDluhDatum];
                                            });
                                        }}
                                        disabled={!form.getFieldValue('pocdnikup')}
                                    />
                                )}
                            </Title>
                            {!dluhopisSecondVariant && (
                                <Paragraph
                                    text="VYPLNTE_DATUMY"
                                    style={{
                                        display: 'block',
                                        marginBottom: 10
                                    }}
                                />
                            )}

                            <Table
                                rowKey={(_, index) => index || 0}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                style={{
                                    margin: '30px 0px'
                                }}
                                columns={[
                                    {
                                        title: (
                                            <>
                                                <Paragraph
                                                    text="*"
                                                    style={{
                                                        color: '#ff4d4f',
                                                        fontSize: 20,
                                                        position: 'relative',
                                                        top: 4,
                                                        marginRight: 4
                                                    }}
                                                />
                                                <TranslateText>ROZHODNE_DATUM_VYPLATY</TranslateText>{' '}
                                                <InfoTooltip
                                                    title={
                                                        <>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_1"
                                                                    style={{
                                                                        fontWeight: 'bold',
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_2"
                                                                    style={{
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_3"
                                                                    style={{
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_4"
                                                                    style={{
                                                                        fontWeight: 'bold',
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                        </>
                                                    }
                                                />
                                            </>
                                        ),
                                        dataIndex: 'datrozhkup',
                                        key: 'datrozhkup',
                                        render: (text: string, _row, i) => {
                                            const currentIndex = (paginationCurrent - 1) * 12 + i;

                                            return (
                                                <DatePicker
                                                    className="TableItem"
                                                    name={`datum${currentIndex}`}
                                                    value={text ? dayjs(text) : undefined}
                                                    status={dodatekDatesErr ? 'error' : undefined}
                                                    onChange={val => {
                                                        setDodatekDatesErr(false);
                                                        setDodatekDates(pv => {
                                                            return pv?.map((e, index) =>
                                                                index === currentIndex && e
                                                                    ? ({
                                                                          datrozhkup: val
                                                                              ? val.format('YYYY-MM-DD')
                                                                              : undefined,

                                                                          datexkup: val
                                                                              ? dayjs(val)
                                                                                    .subtract(
                                                                                        Number(
                                                                                            form.getFieldValue(
                                                                                                'pocdnikup'
                                                                                            ) || 0
                                                                                        ) - 1,
                                                                                        'days'
                                                                                    )
                                                                                    .format('YYYY-MM-DD')
                                                                              : e.datexkup
                                                                      } as IDodatekDluhDatum)
                                                                    : e
                                                            );
                                                        });
                                                    }}
                                                    pickerValue={pickerVal || dayjs(text || undefined)}
                                                    onPanelChange={val => {
                                                        setPickerVal(val);
                                                    }}
                                                    onOpenChange={val => {
                                                        !val && setPickerVal(undefined);
                                                    }}
                                                    format={['DD.MM.YYYY', 'D.M.YYYY']}
                                                    style={{
                                                        width: '100%'
                                                    }}
                                                    placeholder={getLanguageObject(language).VYBERTE_PLACEHOLDER}
                                                />
                                            );
                                        }
                                    },
                                    {
                                        title: (
                                            <>
                                                <Paragraph
                                                    text="*"
                                                    style={{
                                                        color: '#ff4d4f',
                                                        fontSize: 20,
                                                        position: 'relative',
                                                        top: 4,
                                                        marginRight: 4
                                                    }}
                                                />
                                                <TranslateText>DATUM_EX_KUPONU</TranslateText>
                                            </>
                                        ),
                                        dataIndex: 'datexkup',
                                        key: 'datexkup',
                                        render: (text: string, row, i) => {
                                            const currentIndex = (paginationCurrent - 1) * 12 + i;

                                            return dluhopisSecondVariant ? (
                                                text ? (
                                                    dayjs(row.datexkup).format('DD.MM.YYYY')
                                                ) : (
                                                    ''
                                                )
                                            ) : (
                                                <DatePicker
                                                    className="TableItem"
                                                    name={`datumEx${currentIndex}`}
                                                    value={text ? dayjs(text) : undefined}
                                                    status={dodatekDatesErr ? 'error' : undefined}
                                                    onChange={val => {
                                                        setDodatekDatesErr(false);
                                                        setDodatekDates(pv => {
                                                            return pv?.map((e, index) =>
                                                                index === currentIndex && e
                                                                    ? ({
                                                                          datrozhkup: e.datrozhkup,
                                                                          datexkup: val
                                                                              ? val.format('YYYY-MM-DD')
                                                                              : undefined
                                                                      } as IDodatekDluhDatum)
                                                                    : e
                                                            );
                                                        });
                                                    }}
                                                    format={['DD.MM.YYYY', 'D.M.YYYY']}
                                                    style={{
                                                        width: '100%'
                                                    }}
                                                    placeholder={getLanguageObject(language).VYBERTE_PLACEHOLDER}
                                                    pickerValue={pickerVal || dayjs(text || undefined)}
                                                    onPanelChange={val => {
                                                        setPickerVal(val);
                                                    }}
                                                    onOpenChange={val => {
                                                        !val && setPickerVal(undefined);
                                                    }}
                                                />
                                            );
                                        }
                                    },
                                    {
                                        dataIndex: 'orignazev',
                                        key: 'orignazev',
                                        render: (_a, _b, index) => {
                                            const currentIndex = (paginationCurrent - 1) * 12 + index;
                                            return dluhopisSecondVariant ? (
                                                <ButtonComponent
                                                    className="TableItem"
                                                    id={`c2MinusBtn${currentIndex}`}
                                                    htmlType="button"
                                                    type="default"
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 50
                                                    }}
                                                    icon={<MinusOutlined />}
                                                    onClick={() => {
                                                        setTimeout(
                                                            () =>
                                                                form.resetFields([
                                                                    `datum${currentIndex}`,
                                                                    `datumEx${currentIndex}`
                                                                ]),
                                                            0
                                                        );
                                                        setDodatekDates(pv => {
                                                            return pv?.filter((_e, i) => i !== currentIndex);
                                                        });
                                                    }}
                                                />
                                            ) : null;
                                        }
                                    }
                                ]}
                                dataSource={dodatekDates}
                                pagination={{
                                    pageSize: 12,
                                    current: paginationCurrent,
                                    onChange: page => setPaginationCurrent(page),
                                    showSizeChanger: false
                                }}
                                rowClassName={(_a, i) => {
                                    const classes = [];
                                    if (i % 2) classes.push('tableRowGray');
                                    return classes.join(' ');
                                }}
                            />
                            <div
                                style={{
                                    marginTop: 20
                                }}
                            />
                            <SelectComponent
                                name="typdatjist"
                                labelParagraph="ROZHODNE_DATUM_VYPLATA_PREDCHAZIO"
                                isFormItem={true}
                                data={[
                                    {
                                        value: 'KUPON',
                                        labelcz: 'DODATEK_DLUH_VYPLATA_TYP_KUPON'
                                    },
                                    {
                                        value: 'JINE',
                                        labelcz: 'DODATEK_DLUH_VYPLATA_TYP_JINE'
                                    }
                                ]}
                                defaultValue={data?.typdatjist}
                                onChange={val => {
                                    const datjist = form.getFieldValue('datjist');
                                    const pocetdni = form.getFieldValue(val === 'JINE' ? 'pocdnijist' : 'pocdnikup');

                                    datjist &&
                                        (pocetdni || pocetdni == 0) &&
                                        form.setFieldsValue({
                                            datexjist: dayjs(datjist).subtract(Number(pocetdni) - 1, 'days')
                                        });

                                    rerender(pv => !pv);
                                }}
                            />
                            {form.getFieldValue('typdatjist') === 'JINE' && (
                                <InputNumberComponent
                                    name="pocdnijist"
                                    label="POCET_DNI"
                                    initialValue={data?.pocdnijist}
                                    onChangeCustom={val => {
                                        const datjist = form.getFieldValue('datjist');
                                        datjist &&
                                            val &&
                                            form.setFieldsValue({
                                                datexjist: dayjs(datjist).subtract(Number(val) - 1, 'days')
                                            });
                                    }}
                                />
                            )}
                            <Row justify="space-between">
                                <Col span={10}>
                                    <DatePickerComponent
                                        name="datjist"
                                        initialValue={data?.datjist ? dayjs(data.datjist) : undefined}
                                        disabledDate={current => current < dayjs()}
                                        label="ROZHODNE_DATUM_JISTANA"
                                        infoTooltipText={
                                            <>
                                                <Row>
                                                    <Paragraph
                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_1"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </Row>
                                                <Row>
                                                    <Paragraph
                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_2"
                                                        style={{
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </Row>
                                                <Row>
                                                    <Paragraph
                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_3"
                                                        style={{
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </Row>
                                                <Row>
                                                    <Paragraph
                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_4"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                </Row>
                                            </>
                                        }
                                        onChange={val => {
                                            const typdatjist = form.getFieldValue('typdatjist');
                                            const pocetdni = form.getFieldValue(
                                                typdatjist === 'JINE' ? 'pocdnijist' : 'pocdnikup'
                                            );
                                            typdatjist &&
                                                (pocetdni || pocetdni == 0) &&
                                                val &&
                                                form.setFieldsValue({
                                                    datexjist: val.subtract(Number(pocetdni) - 1, 'days')
                                                });
                                        }}
                                    />
                                </Col>
                                <Col span={10}>
                                    <DatePickerComponent
                                        name="datexjist"
                                        initialValue={data?.datexjist ? dayjs(data.datexjist) : undefined}
                                        disabledDate={current => current < dayjs()}
                                        label="DATUM_EX_JISTANA"
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                    {form.getFieldValue('zaruceniemise') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_ZARUCENI_EMISE" />
                            <SelectComponent
                                isFormItem={true}
                                name="zaruceniemisep"
                                defaultValue={data?.zaruceniemisep}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_ZARUCENI_EMISE_P"
                                data={zarucenieEmiseSelectAlt}
                            />
                            <SelectComponent
                                isFormItem={true}
                                name="zaruceniemisen"
                                defaultValue={data?.zaruceniemisen}
                                labelParagraph="PRIKAZ_ZMENA_UDAJU_ZARUCENI_EMISE_N"
                                data={zarucenieEmiseSelectAlt}
                            />
                        </>
                    )}
                    {form.getFieldValue('jiny') && (
                        <>
                            <Title level={4} dividerTitle={true} text="PRIKAZ_ZMENA_UDAJU_TYP_JINY" />
                            <InputTextArea
                                rows={4}
                                name="jinyudaj"
                                initialValue={data?.jinyudaj}
                                label="PRIKAZ_ZMENA_UDAJU_TYP_JINY_POPIS"
                            />
                        </>
                    )}

                    <PrilohyTable zadostID={zadostID} required={true} />
                    <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_ZMENA_UDAJU_DLT_TEXT1' : 'PRIKAZ_ZMENA_UDAJU_TEXT1'}
                        style={{
                            display: 'block',
                            marginTop: 10,
                            marginBottom: 10
                        }}
                    />
                    {isDLT && (
                        <Paragraph
                            text="PRIKAZ_ZMENA_UDAJU_DLT_TEXT2"
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                    )}
                    <Paragraph
                        text={isDLT ? 'PRIKAZ_ZMENA_UDAJU_DLT_TEXT3' : 'PRIKAZ_ZMENA_UDAJU_TEXT3'}
                        style={{
                            display: 'block',
                            marginTop: 10,
                            marginBottom: 10
                        }}
                    />
                    <Row
                        style={{
                            marginTop: 26
                        }}
                    >
                        <CheckboxComponent
                            name="souhlas"
                            checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                            onChange={() => setIsChecked(pv => !pv)}
                            checked={isChecked}
                        />
                    </Row>
                    <Row className="button-row">
                        <Col>
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                id="isinPokracovatBtn"
                                disabled={!isChecked}
                                extramessage={dodatekDatesErr ? 'DODATEK_DLUHOPIS_DATUM_ERR2' : ''}
                                htmlType="submit"
                            />
                        </Col>
                    </Row>
                </FormWrapper>
            )}
        </LoadingWrapper>
    );
};

export default ZmenaUdaju;
